import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const SYMBOLS = ['🍒', '🍋', '🍊', '🍇', '7', '💎', '⭐'];

function App() {
  const [isSpinning, setIsSpinning] = useState(false);
  const [reels, setReels] = useState(['7', '7', '7']);
  const [isWinner, setIsWinner] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [spinStates, setSpinStates] = useState([true, true, true]); // リール個別の回転状態

  const spin = () => {
    if (isSpinning) return;
    
    setIsSpinning(true);
    setIsWinner(false);
    setShowCelebration(false);
    setSpinStates([true, true, true]); // すべてのリールが回転中
    
    // 各リールが段階的に止まる（1800ms, 2000ms, 2200ms）
    const stopTimes = [1800, 2000, 2200];
    
    // 確率判定：20%の確率で777になる
    const shouldWin = Math.random() < 0.20;
    
    const newReels = shouldWin 
      ? ['7', '7', '7']
      : [
          SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)],
          SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)],
          SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)]
        ];
    
    // 各リール個別のタイミングでアニメーション終了
    stopTimes.forEach((time, index) => {
      setTimeout(() => {
        setSpinStates(prev => {
          const newStates = [...prev];
          newStates[index] = false; // そのリールが停止
          return newStates;
        });
        
        // 最後のリール（2200ms）の時に全体を完了
        if (index === 2) {
          setReels(newReels);
          setIsSpinning(false);
          
          if (newReels.every(symbol => symbol === '7')) {
            setIsWinner(true);
            setShowCelebration(true);
            playWinSound();
          }
        }
      }, time);
    });
  };

  const playWinSound = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    [440, 554, 659, 880].forEach((freq, index) => {
      setTimeout(() => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      }, index * 200);
    });
  };

  return (
    <main className="min-h-screen relative overflow-hidden bg-gradient-to-br from-yellow-600 via-amber-500 to-yellow-700">
      {/* 背景のマカオ風テクスチャ */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(255,215,0,0.3),transparent_70%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,165,0,0.2),transparent_60%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_80%,rgba(218,165,32,0.25),transparent_70%)]"></div>
      
      {/* 装飾的な金色の粒子エフェクト */}
      <div className="absolute inset-0">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-yellow-300/40 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.5, 1],
              y: [0, -20, 0]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
      
      {/* メインコンテンツ */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center items-center px-4 py-8">
        
        {/* タイトルエリア */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <motion.h1 
              className="text-7xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 via-yellow-100 to-white mb-4 tracking-wider leading-none drop-shadow-2xl"
              style={{ fontFamily: 'Impact, "Arial Black", sans-serif' }}
              animate={showCelebration ? { scale: [1, 1.08, 1] } : {}}
              transition={{ duration: 0.8, repeat: showCelebration ? Infinity : 0 }}
            >
              スロット
            </motion.h1>
            <motion.h2 
              className="text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-b from-amber-200 via-yellow-200 to-yellow-100 tracking-widest"
              style={{ fontFamily: 'Impact, "Arial Black", sans-serif' }}
              animate={showCelebration ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.8, repeat: showCelebration ? Infinity : 0, delay: 0.1 }}
            >
              マシーン
            </motion.h2>
            
            <div className="flex items-center justify-center space-x-4 text-4xl mt-6">
              <span>🎰</span>
              <motion.span 
                className="w-16 h-1 bg-gradient-to-r from-yellow-300 via-yellow-100 to-yellow-300 rounded-full"
                animate={{ scaleX: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              ></motion.span>
              <span>🎰</span>
            </div>
          </motion.div>
          
          <motion.p 
            className="text-yellow-100 text-xl font-bold leading-relaxed mt-6 drop-shadow-lg"
            style={{ fontFamily: '"Trebuchet MS", sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            3つのリールを回して、<br/>
            <span className="text-yellow-200 font-black text-2xl">777</span> の組み合わせを狙え！
          </motion.p>
        </div>

        {/* ゲームエリア */}
        <div className="bg-gradient-to-b from-yellow-800/30 to-amber-900/40 backdrop-blur-md rounded-3xl shadow-2xl shadow-yellow-900/50 p-8 lg:p-12 border-2 border-yellow-400/30">
          <div className="text-center space-y-10">
        
            {/* スロットリール */}
            <div className="flex gap-8 justify-center">
              {reels.map((symbol, index) => (
                <motion.div
                  key={index}
                  className={`relative w-24 h-24 lg:w-28 lg:h-28 rounded-2xl flex items-center justify-center text-4xl lg:text-5xl font-bold transition-all duration-300 ${
                    isWinner 
                      ? 'bg-gradient-to-br from-yellow-200 via-yellow-100 to-white border-4 border-yellow-300 shadow-2xl shadow-yellow-400/80' 
                      : 'bg-gradient-to-br from-yellow-100 via-white to-yellow-50 border-4 border-yellow-400/60 shadow-xl shadow-yellow-600/40'
                  }`}
                  animate={
                    spinStates[index]
                      ? { 
                          // リール回転中：高速で連続回転（rotateXで真正面から始まる）
                          rotateX: [0, 1440],
                          scale: [1, 0.85, 1],
                          y: [0, -15, 0]
                        }
                      : isWinner 
                        ? { 
                            // 当選時：完全に平面（rotateX: 0）
                            rotateX: 0,
                            scale: [1, 1.12, 1],
                            boxShadow: [
                              '0 15px 60px rgba(255, 215, 0, 0.4)',
                              '0 20px 80px rgba(255, 215, 0, 0.8)',
                              '0 15px 60px rgba(255, 215, 0, 0.4)'
                            ]
                          }
                        : {
                            // 停止時：完全に平面（rotateX: 0）
                            rotateX: 0,
                            scale: 1,
                            y: 0,
                            boxShadow: '0 10px 40px rgba(255, 193, 7, 0.3)'
                          }
                  }
                  transition={
                    spinStates[index]
                      ? { 
                          // 回転中：早く回る（720度を0.3秒で2回転）
                          duration: 0.3, 
                          repeat: Infinity, 
                          ease: "linear"
                        }
                      : isWinner 
                        ? { duration: 0.6, ease: "easeOut" }
                        : { duration: 0.5, ease: "easeOut" }
                  }
                  whileHover={!isSpinning ? { 
                    scale: 1.08,
                    boxShadow: '0 15px 50px rgba(255, 193, 7, 0.5)'
                  } : {}}
                >
                  {/* 内側の黄金グロー効果 */}
                  {isWinner && (
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-yellow-300/40 to-yellow-200/50 animate-pulse"></div>
                  )}
                  
                  {/* 装飾的な輝きエフェクト */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-transparent via-white/20 to-transparent"></div>
                  
                  {/* シンボル */}
                  <span className="relative z-10 drop-shadow-lg">
                    {spinStates[index] ? (
                      <motion.span
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 0.3, repeat: Infinity, ease: "linear" }}
                        className="text-yellow-600"
                      >
                        ❓
                      </motion.span>
                    ) : (
                      symbol
                    )}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* 当選エフェクト */}
            {isWinner && (
              <motion.div
                initial={{ opacity: 0, scale: 0.7, y: 40 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="space-y-6"
              >
                <motion.div
                  className="text-6xl lg:text-7xl font-black"
                  style={{ fontFamily: 'Impact, "Arial Black", sans-serif' }}
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 8, -8, 0] 
                  }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  🎉 ジャックポット！ 🎉
                </motion.div>
                
                <motion.div
                  className="relative"
                  animate={{ y: [0, -12, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div 
                    className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-yellow-100 to-white text-4xl lg:text-5xl font-black tracking-widest drop-shadow-2xl"
                    style={{ fontFamily: 'Impact, "Arial Black", sans-serif' }}
                  >
                    ✨ 777 WINNER ✨
                  </div>
                  
                  {/* 背景グロー */}
                  <div className="absolute inset-0 text-yellow-300/50 text-4xl lg:text-5xl font-black tracking-widest blur-lg">
                    ✨ 777 WINNER ✨
                  </div>
                </motion.div>
                
                {/* 祝福パーティクル */}
                <div className="absolute inset-0 pointer-events-none">
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute text-3xl"
                      style={{
                        left: `${15 + i * 12}%`,
                        top: `${25 + (i % 3) * 20}%`,
                      }}
                      animate={{
                        y: [-30, -60, -30],
                        opacity: [0, 1, 0],
                        scale: [0.5, 1.5, 0.5],
                        rotate: [0, 360]
                      }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        delay: i * 0.4,
                        ease: "easeInOut"
                      }}
                    >
                      {i % 2 === 0 ? '✨' : '💰'}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
        
            {/* スピンボタン */}
            <motion.button
              onClick={spin}
              disabled={isSpinning}
              className={`relative overflow-hidden px-12 py-5 text-xl font-black rounded-2xl transition-all duration-300 ${
                isSpinning
                  ? 'bg-yellow-600/40 text-yellow-200/60 cursor-not-allowed border-2 border-yellow-400/30'
                  : 'bg-gradient-to-r from-yellow-800 to-amber-900 text-yellow-100 hover:from-yellow-900 hover:to-amber-950 shadow-2xl shadow-yellow-900/60 border-2 border-yellow-400/50'
              }`}
              style={{ fontFamily: 'Impact, "Arial Black", sans-serif' }}
              whileHover={!isSpinning ? { 
                scale: 1.08,
                boxShadow: '0 25px 60px rgba(153, 102, 0, 0.4)'
              } : {}}
              whileTap={!isSpinning ? { scale: 0.95 } : {}}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {/* ボタン内の黄金シマー効果 */}
              {!isSpinning && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-300/30 to-transparent -translate-x-full"
                  animate={{ translateX: ['100%', '100%', '-100%'] }}
                  transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2 }}
                />
              )}
              
              <span className="relative z-10 flex items-center justify-center space-x-3 tracking-wider">
                {isSpinning ? (
                  <>
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                      className="text-2xl"
                    >
                      ⚡
                    </motion.span>
                    <span>回転中...</span>
                  </>
                ) : (
                  <>
                    <span className="text-2xl">🎲</span>
                    <span>スピン!</span>
                  </>
                )}
              </span>
            </motion.button>
            
            {/* ゲームルール */}
            <motion.div 
              className="text-yellow-100 text-lg font-bold leading-relaxed"
              style={{ fontFamily: '"Trebuchet MS", sans-serif' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.7 }}
            >
              <div className="flex items-center justify-center space-x-3 mb-3">
                <span className="w-12 h-0.5 bg-gradient-to-r from-transparent to-yellow-300"></span>
                <span className="text-yellow-200 font-black tracking-widest">ルール</span>
                <span className="w-12 h-0.5 bg-gradient-to-l from-transparent to-yellow-300"></span>
              </div>
              <p className="drop-shadow-lg">
                3つのリールで <span className="text-yellow-200 font-black text-xl">777</span> が揃うと<br/>
                <span className="text-yellow-100 font-black">ジャックポット獲得！</span>
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default App;
